const TOKEN_KEY = 'ntr.token';
const API_BASE = process.env.REACT_APP_API_BASE || '';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(message, status, issues, payload) {
    super(message);
    this.status = status;
    this.issues = issues || [];
    // The full JSON body, for routes that attach more than an error string —
    // e.g. login's { needsVerification, email } when the account isn't verified yet.
    this.payload = payload || {};
  }

  /** Field-level messages keyed by field name, for inline form errors. */
  get fieldErrors() {
    return this.issues.reduce((acc, i) => ({ ...acc, [i.field]: i.message }), {});
  }
}

async function request(method, path, { body, form, signal } = {}) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body) headers['Content-Type'] = 'application/json';

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      signal,
      body: form || (body ? JSON.stringify(body) : undefined),
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError('Could not reach the server. Please check your connection.', 0);
  }

  if (res.status === 204) return null;

  const text = await res.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!res.ok) {
    // An expired or revoked token should drop the session rather than loop.
    if (res.status === 401 && token) setToken(null);
    // A non-JSON 5xx means the request never reached the API — in development
    // that is the dev server reporting it could not proxy to the backend.
    const message =
      payload?.error ||
      (res.status >= 500 ? 'Could not reach the server. Is the API running?' : 'Something went wrong.');
    throw new ApiError(message, res.status, payload?.issues, payload);
  }
  return payload;
}

export const api = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  put: (path, body, opts) => request('PUT', path, { ...opts, body }),
  patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
  del: (path, opts) => request('DELETE', path, opts),
  upload: (path, formData) => request('POST', path, { form: formData }),
};

/** Downloads a protected file through an authenticated request. */
export async function openDocument(documentId, { download = false } = {}) {
  const res = await fetch(`${API_BASE}/api/files/documents/${documentId}?download=${download ? 1 : 0}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new ApiError('This document could not be opened.', res.status);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
